import { MINUTES_PER_DAY, type DurationMode } from '@/src/renderer/lib/db/models'

export { MINUTES_PER_DAY }

/**
 * Parse natural language duration input into minutes.
 *
 * Supported formats:
 * - "10m", "10min", "10 Min", "10 Minuten" → 10
 * - "2h", "2std", "2 Stunden" → 120
 * - "1h30m", "1h 30m", "1:30", "1 Stunde 30", "1 Stunde 30 Minuten" → 90
 * - "30" (bare number) → 30 minutes
 *
 * @returns minutes as integer, or null if input is invalid
 */
export function parseDuration(input: string): number | null {
  const trimmed = input.trim().toLowerCase()
  if (!trimmed) return null

  // H:MM format (e.g. "1:30", "0:10")
  const colonMatch = trimmed.match(/^(\d+):(\d{1,2})$/)
  if (colonMatch) {
    const hours = parseInt(colonMatch[1], 10)
    const mins = parseInt(colonMatch[2], 10)
    if (mins >= 60) return null
    return hours * 60 + mins
  }

  // Combined hours + minutes (e.g. "1h30m", "1h 30", "2 Stunden 30 Minuten")
  const combinedMatch = trimmed.match(
    /^(\d+)\s*(?:h|std|stunde|stunden)\s*(\d+)\s*(?:m|min|minuten|minuten)?$/
  )
  if (combinedMatch) {
    const hours = parseInt(combinedMatch[1], 10)
    const mins = parseInt(combinedMatch[2], 10)
    if (mins >= 60) return null
    return hours * 60 + mins
  }

  // Hours only (e.g. "2h", "2 Stunden", "1 Stunde")
  const hourMatch = trimmed.match(/^(\d+)\s*(?:h|std|stunde|stunden)$/)
  if (hourMatch) {
    return parseInt(hourMatch[1], 10) * 60
  }

  // Minutes only (e.g. "10m", "10 Min", "10 Minuten")
  const minMatch = trimmed.match(/^(\d+)\s*(?:m|min|minuten?)$/)
  if (minMatch) {
    return parseInt(minMatch[1], 10)
  }

  // Bare number → minutes
  const bareNumber = trimmed.match(/^(\d+)$/)
  if (bareNumber) {
    return parseInt(bareNumber[1], 10)
  }

  return null
}

/**
 * Liest eine Dauer in Tagen: „3 Tage“, „1 Tag“, „3t“, „3d“, „2 days“.
 * Nur ganze Tage — einen halben gibt es als Einheit nicht, dafür sind Stunden da.
 *
 * @returns Anzahl Tage, oder null, wenn die Eingabe keine Tage nennt
 */
export function parseDayDuration(input: string): number | null {
  const match = input.trim().toLowerCase().match(/^(\d+)\s*(?:t|tag|tage|d|day|days)$/)
  if (!match) return null
  const days = parseInt(match[1], 10)
  return days > 0 ? days : null
}

/**
 * Format minutes into a compact display string.
 *
 * Tage stehen wie Stunden abgekürzt; die Einheit kommt aus der Sprache
 * (`taskCreation.durationDayUnit`: Deutsch „t“, Englisch „d“).
 *
 * @example formatDuration(10)  → "10m"
 * @example formatDuration(60)  → "1h"
 * @example formatDuration(90)  → "1h 30m"
 * @example formatDuration(480) → "8h"
 * @example formatDuration(1440, 'days', 't') → "3t"
 */
export function formatDuration(minutes: number, mode?: DurationMode | null, dayUnit = 'd'): string {
  if (mode === 'days' && minutes > 0 && minutes % MINUTES_PER_DAY === 0) return `${minutes / MINUTES_PER_DAY}${dayUnit}`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}
